import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  ArrowLeft,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  Play,
  RefreshCw,
  TrendingUp,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import type { ActionStatus, CrawlerAccess, Impact, SearchSummary, SeoPlanAction, SeoPlanRun } from './api'
import {
  useStartAllSeoPlanRuns,
  useSeoPlanInstruction,
  useSeoPlanPages,
  useSeoPlanQueries,
  useSeoPlanSite,
  useSeoPlanSites,
  useStartSeoPlanRun,
  useUpdateSeoPlanAction,
} from './hooks'

const IMPACT_LABELS: Record<Impact, string> = { high: 'Hoog', medium: 'Middel', low: 'Laag' }
const IMPACT_STYLES: Record<Impact, string> = {
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  medium: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
}
const STATUS_LABELS: Record<ActionStatus, string> = {
  open: 'Open',
  in_progress: 'Bezig',
  ignored: 'Genegeerd',
  resolved: 'Opgelost',
}
// The awesome-seo loop, in its own order.
const PHASE_ORDER = ['intentie', 'content', 'toegang', 'ervaring', 'zichtbaarheid', 'meten']

function ago(iso: string) {
  return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: nl })
}

function RunLine({ run }: { run: SeoPlanRun | null }) {
  if (!run) return <span>Nog niet geanalyseerd</span>
  if (run.status === 'running')
    return (
      <span className="inline-flex items-center gap-1">
        <Spinner size="sm" /> Analyse loopt (gestart {ago(run.started_at)})
      </span>
    )
  if (run.status === 'failed')
    return (
      <span className="text-red-600 dark:text-red-400" title={run.error ?? undefined}>
        Laatste analyse mislukt {ago(run.started_at)}
      </span>
    )
  return (
    <span>
      Geanalyseerd {ago(run.finished_at ?? run.started_at)} · {run.pages_crawled} pagina&apos;s
    </span>
  )
}

const SEARCH_ERRORS: Record<string, string> = {
  not_connected: 'Search Console is niet gekoppeld voor deze site',
  no_access: 'Geen toegang tot de Search Console-property',
  api_error: 'Search Console was niet bereikbaar bij de laatste analyse',
}

/** Percent change, or null when there is nothing to compare with. */
function change(now: number | null, before: number | null): number | null {
  if (now === null || before === null || before === 0) return null
  return Math.round(((now - before) / before) * 100)
}

function Delta({ value, inverse = false }: { value: number | null; inverse?: boolean }) {
  if (value === null || value === 0) return null
  const good = inverse ? value < 0 : value > 0
  return (
    <span className={cn('ml-1 text-xs', good ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
      {value > 0 ? '+' : ''}
      {value}%
    </span>
  )
}

const nf = new Intl.NumberFormat('nl-BE')

function SearchLine({ search }: { search: SearchSummary | null }) {
  if (!search) return null
  if (search.error) return <span className="text-amber-600 dark:text-amber-400">{SEARCH_ERRORS[search.error]}</span>
  return (
    <span>
      {nf.format(search.clicks ?? 0)} klikken
      <Delta value={change(search.clicks, search.clicks_previous)} /> · {nf.format(search.impressions ?? 0)}{' '}
      vertoningen <Delta value={change(search.impressions, search.impressions_previous)} /> · 28 dagen
    </span>
  )
}

function SearchCard({ search }: { search: SearchSummary | null }) {
  if (!search) return null
  if (search.error)
    return (
      <Card>
        <CardContent className="py-4 text-sm">
          <span className="text-amber-600 dark:text-amber-400">{SEARCH_ERRORS[search.error]}.</span>{' '}
          <span className="text-muted-foreground">{search.detail}</span>
        </CardContent>
      </Card>
    )
  const positionChange =
    search.position !== null && search.position_previous !== null
      ? Math.round((search.position - search.position_previous) * 10) / 10
      : null
  const stats: [string, string, ReactNode][] = [
    ['Klikken', nf.format(search.clicks ?? 0), <Delta value={change(search.clicks, search.clicks_previous)} />],
    [
      'Vertoningen',
      nf.format(search.impressions ?? 0),
      <Delta value={change(search.impressions, search.impressions_previous)} />,
    ],
    ['CTR', `${((search.ctr ?? 0) * 100).toFixed(1)}%`, null],
    [
      'Gem. positie',
      search.position?.toFixed(1) ?? '—',
      positionChange ? (
        <span className={cn('ml-1 text-xs', positionChange < 0 ? 'text-green-600' : 'text-red-600')}>
          {positionChange > 0 ? '+' : ''}
          {positionChange}
        </span>
      ) : null,
    ],
  ]
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map(([label, value, delta]) => (
            <div key={label}>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold">
                {value}
                {delta}
              </p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Search Console ({search.property}), {search.window?.[0]} t/m {search.window?.[1]}, tegenover de 28
          dagen ervoor. Enkel de pagina&apos;s van deze site.
        </p>
      </CardContent>
    </Card>
  )
}

const PURPOSE_LABELS: Record<string, string> = {
  search: 'Zoekmachine',
  ai_search: 'AI-zoeken',
  training: 'Training',
}

/** Per crawler: may it (robots.txt) and did the homepage answer it. */
function CrawlerCard({ access }: { access: CrawlerAccess | null }) {
  const [open, setOpen] = useState(false)
  if (!access) return null
  const answer = (b: CrawlerAccess['bots'][number]) => {
    if (b.status === undefined) return <span className="text-muted-foreground">enkel robots.txt</span>
    if (b.challenged) return <span className="text-red-600 dark:text-red-400">challenge</span>
    if (b.status === null) return <span className="text-red-600 dark:text-red-400">{b.error ?? 'geen antwoord'}</span>
    return (
      <span className={b.status >= 400 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
        HTTP {b.status}
      </span>
    )
  }
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm font-medium">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Toegang voor zoek- en AI-crawlers
        </button>
        {open && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-4 font-medium">Crawler</th>
                    <th className="py-1 pr-4 font-medium">Doel</th>
                    <th className="py-1 pr-4 font-medium">robots.txt</th>
                    <th className="py-1 font-medium">Homepage</th>
                  </tr>
                </thead>
                <tbody>
                  {access.bots.map((b) => (
                    <tr key={b.bot} className="border-t">
                      <td className="py-1 pr-4 font-mono text-xs">{b.bot}</td>
                      <td className="py-1 pr-4">{PURPOSE_LABELS[b.purpose]}</td>
                      <td className={cn('py-1 pr-4', !b.robots_allowed && 'text-amber-600 dark:text-amber-400')}>
                        {b.robots_allowed ? 'toegelaten' : 'geweigerd'}
                      </td>
                      <td className="py-1">{answer(b)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Opgehaald vanaf het dashboard met de user-agent van elke bot; een gewone browser kreeg HTTP{' '}
              {access.baseline.status ?? '—'}. Een WAF herkent echte crawlers aan hun IP-adres, dus een afwijking
              hier is een signaal om na te kijken, geen bewijs. Trainingsbots weigeren is een keuze, geen fout.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function useRun() {
  const start = useStartSeoPlanRun()
  const run = (dossierId: number, name: string) =>
    start.mutate(dossierId, {
      onSuccess: (r) => toast.success(`${name}: ${r.pages_crawled} pagina's geanalyseerd`),
      onError: (e) => toast.error(`${name}: ${e.message}`),
    })
  const isRunning = (dossierId: number, lastRun: SeoPlanRun | null) =>
    (start.isPending && start.variables === dossierId) || lastRun?.status === 'running'
  return { run, isRunning }
}

function RunButton({
  dossierId,
  name,
  lastRun,
  runner,
}: {
  dossierId: number
  name: string
  lastRun: SeoPlanRun | null
  runner: ReturnType<typeof useRun>
}) {
  const running = runner.isRunning(dossierId, lastRun)
  return (
    <Button size="sm" variant="outline" disabled={running} onClick={() => runner.run(dossierId, name)}>
      {running ? (
        <RefreshCw className="mr-1 h-4 w-4 animate-spin" />
      ) : (
        <Play className="mr-1 h-4 w-4" />
      )}
      {running ? 'Bezig…' : lastRun ? 'Opnieuw analyseren' : 'Analyseer nu'}
    </Button>
  )
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function Overview({ onOpen }: { onOpen: (id: number) => void }) {
  const { data: sites, isLoading } = useSeoPlanSites()
  const runner = useRun()

  if (isLoading)
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )

  return (
    <div className="space-y-3">
      {(sites ?? []).map((site) => {
        const open = site.open_high + site.open_medium + site.open_low
        return (
          <Card key={site.dossier_id}>
            <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
              <button
                onClick={() => onOpen(site.dossier_id)}
                className="flex min-w-0 flex-1 flex-col gap-1 text-left"
              >
                <span className="flex items-center gap-2 font-medium">
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{site.name}</span>
                </span>
                <span className="truncate text-xs text-muted-foreground">{site.url}</span>
                <span className="text-xs text-muted-foreground">
                  <RunLine run={site.last_run} />
                </span>
                {site.search && (
                  <span className="text-xs text-muted-foreground">
                    <SearchLine search={site.search} />
                  </span>
                )}
                {site.last_run?.status !== undefined && (
                  <span className="flex flex-wrap items-center gap-2 pt-1">
                    {site.open_high > 0 && <Badge className={IMPACT_STYLES.high}>{site.open_high} hoog</Badge>}
                    {site.open_medium > 0 && (
                      <Badge className={IMPACT_STYLES.medium}>{site.open_medium} middel</Badge>
                    )}
                    {site.open_low > 0 && <Badge className={IMPACT_STYLES.low}>{site.open_low} laag</Badge>}
                    {open === 0 && site.last_run?.status === 'done' && (
                      <span className="inline-flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" /> Niets open
                      </span>
                    )}
                    {site.resolved > 0 && (
                      <span className="text-sm text-muted-foreground">{site.resolved} opgelost</span>
                    )}
                  </span>
                )}
                {site.top_actions.length > 0 && (
                  <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-sm">
                    {site.top_actions.map((title) => (
                      <li key={title}>{title}</li>
                    ))}
                  </ol>
                )}
              </button>
              <div className="flex shrink-0 gap-2">
                <RunButton
                  dossierId={site.dossier_id}
                  name={site.name}
                  lastRun={site.last_run}
                  runner={runner}
                />
                <Button size="sm" onClick={() => onOpen(site.dossier_id)}>
                  Plan
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// ─── One site ─────────────────────────────────────────────────────────────────

function ActionCard({ action, phaseLabel }: { action: SeoPlanAction; phaseLabel: string }) {
  const [open, setOpen] = useState(false)
  const update = useUpdateSeoPlanAction()
  const pages = action.evidence.pages ?? []
  const setStatus = (status: Exclude<ActionStatus, 'resolved'>) =>
    update.mutate(
      { id: action.id, status },
      { onError: (e) => toast.error(`Bijwerken mislukt: ${e.message}`) },
    )

  return (
    <Card className={cn(action.status === 'resolved' && 'opacity-70')}>
      <CardContent className="space-y-2 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={IMPACT_STYLES[action.impact]}>{IMPACT_LABELS[action.impact]}</Badge>
              <Badge variant="outline" className="text-xs">
                {phaseLabel}
              </Badge>
              {action.status !== 'open' && (
                <Badge variant="secondary" className="text-xs">
                  {STATUS_LABELS[action.status]}
                  {action.status === 'resolved' && action.resolved_at && ` ${ago(action.resolved_at)}`}
                </Badge>
              )}
            </div>
            <p className="font-medium">{action.title}</p>
          </div>
          {action.status !== 'resolved' && (
            <div className="flex shrink-0 gap-2">
              {action.status !== 'in_progress' && (
                <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => setStatus('in_progress')}>
                  Bezig
                </Button>
              )}
              {action.status !== 'ignored' ? (
                <Button size="sm" variant="ghost" disabled={update.isPending} onClick={() => setStatus('ignored')}>
                  Negeren
                </Button>
              ) : (
                <Button size="sm" variant="ghost" disabled={update.isPending} onClick={() => setStatus('open')}>
                  Heropenen
                </Button>
              )}
            </div>
          )}
        </div>

        <p className="text-sm">{action.fix}</p>

        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          {action.guidance_url && (
            <a
              href={action.guidance_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {action.guidance_label ?? 'Richtlijn'}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {pages.length > PREVIEW_LINES && (
            <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 hover:text-foreground">
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {open ? 'Minder tonen' : `Alle ${action.affected_count} tonen`}
            </button>
          )}
        </div>

        {pages.length > 0 && (
          <ul className="space-y-0.5 rounded-md bg-muted p-2 font-mono text-xs">
            {(open ? pages : pages.slice(0, PREVIEW_LINES)).map((line) => (
              <li key={line} className="break-all">
                {line}
              </li>
            ))}
            {open && (action.evidence.total ?? 0) > pages.length && (
              <li className="font-sans text-muted-foreground">
                … en {(action.evidence.total ?? 0) - pages.length} meer
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

const PREVIEW_LINES = 3

function InstructionPanel({ dossierId, onClose }: { dossierId: number; onClose: () => void }) {
  const { data, isLoading, isError, error } = useSeoPlanInstruction(dossierId, true)
  const copy = async () => {
    if (!data) return
    try {
      await navigator.clipboard.writeText(data.text)
      toast.success('Gekopieerd — plak het in Claude of in Claude Code in de repo van de site')
    } catch {
      toast.error('Kopiëren mislukt; selecteer de tekst handmatig')
    }
  }
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 font-medium">
            <Bot className="h-4 w-4" />
            Plan voor Claude of Claude Code
            {data && <span className="text-xs font-normal text-muted-foreground">· {data.actions} actie(s)</span>}
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={copy} disabled={!data}>
              <Copy className="mr-1 h-4 w-4" />
              Kopiëren
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>
              Sluiten
            </Button>
          </div>
        </div>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner />
          </div>
        ) : isError || !data ? (
          <p className="text-sm text-red-600">{(error as Error | null)?.message ?? 'Instructie kon niet opgehaald worden.'}</p>
        ) : (
          <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">{data.text}</pre>
        )}
      </CardContent>
    </Card>
  )
}

function fmt(value: number | null | undefined, digits = 0) {
  return value === null || value === undefined ? '—' : value.toLocaleString('nl-BE', { maximumFractionDigits: digits })
}

function path(url: string) {
  try {
    const u = new URL(url)
    return u.pathname + u.search || '/'
  } catch {
    return url
  }
}

/** Every page of the last analysis, with what Google says about it. */
function PagesTable({ dossierId }: { dossierId: number }) {
  const { data, isLoading } = useSeoPlanPages(dossierId, true)
  const [openUrl, setOpenUrl] = useState<string | null>(null)
  if (isLoading)
    return (
      <div className="flex h-24 items-center justify-center">
        <Spinner />
      </div>
    )
  if (!data?.length) return <p className="text-sm text-muted-foreground">Nog geen pagina&apos;s geanalyseerd.</p>
  return (
    <Card>
      <CardContent className="overflow-x-auto py-2">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-2 pr-3 font-medium">Pagina</th>
              <th className="py-2 pr-3 text-right font-medium">Klikken</th>
              <th className="py-2 pr-3 text-right font-medium">Vertoningen</th>
              <th className="py-2 pr-3 text-right font-medium">Positie</th>
              <th className="py-2 pr-3 font-medium">Indexering</th>
              <th className="py-2 pr-3 text-right font-medium">Woorden</th>
              <th className="py-2 text-right font-medium">Snelheid</th>
            </tr>
          </thead>
          <tbody>
            {data.map((p) => {
              const open = openUrl === p.url
              return (
                <Fragment key={p.url}>
                  <tr
                    onClick={() => setOpenUrl(open ? null : p.url)}
                    className="cursor-pointer border-t hover:bg-muted/50"
                  >
                    <td className="max-w-[22rem] py-2 pr-3">
                      <div className="flex items-center gap-1">
                        {open ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                        <span className="truncate font-mono text-xs">{path(p.url)}</span>
                        {p.status !== 200 && <Badge className={IMPACT_STYLES.high}>{p.status ?? 'fout'}</Badge>}
                        {p.noindex && <Badge variant="outline">noindex</Badge>}
                      </div>
                      <div className="truncate pl-4 text-xs text-muted-foreground">{p.title ?? '— geen titel —'}</div>
                    </td>
                    <td className="py-2 pr-3 text-right">{fmt(p.clicks)}</td>
                    <td className="py-2 pr-3 text-right">{fmt(p.impressions)}</td>
                    <td className="py-2 pr-3 text-right">{fmt(p.position, 1)}</td>
                    <td
                      className={cn(
                        'py-2 pr-3 text-xs',
                        p.indexing && !/indexed|PASS/i.test(p.indexing) && 'text-amber-600 dark:text-amber-400',
                        p.indexing && /not indexed/i.test(p.indexing) && 'text-red-600 dark:text-red-400',
                      )}
                    >
                      {p.indexing ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-right">{fmt(p.words)}</td>
                    <td className="py-2 text-right">
                      {p.speed_score !== null ? (
                        <span className={p.speed_score < 50 ? 'text-red-600' : p.speed_score < 90 ? 'text-amber-600' : 'text-green-600'}>
                          {p.speed_score}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-muted/30">
                      <td colSpan={7} className="space-y-1 px-5 py-3 text-xs">
                        <p>
                          <a href={p.url} target="_blank" rel="noopener noreferrer" className="break-all text-primary hover:underline">
                            {p.url}
                          </a>
                        </p>
                        <p><span className="text-muted-foreground">Titel:</span> {p.title ?? '—'}</p>
                        <p><span className="text-muted-foreground">Meta description:</span> {p.description ?? '— ontbreekt —'}</p>
                        <p><span className="text-muted-foreground">H1:</span> {p.h1 ?? '— ontbreekt —'}</p>
                        <p className="text-muted-foreground">
                          {p.inlinks} interne link(s) ernaartoe · {p.in_sitemap ? 'in de sitemap' : 'niet in de sitemap'}
                          {p.lcp_ms !== null && ` · LCP ${(p.lcp_ms / 1000).toFixed(1)}s (Lighthouse, mobiel)`}
                          {p.ctr !== null && ` · CTR ${(p.ctr * 100).toFixed(1)}%`}
                        </p>
                        {p.top_queries.length > 0 && (
                          <p>
                            <span className="text-muted-foreground">Zoekopdrachten:</span>{' '}
                            {p.top_queries.map((q) => `„${q.query}” (pos ${q.position}, ${q.impressions}×)`).join(' · ')}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

/** The searches the site is shown for. */
function QueriesTable({ dossierId }: { dossierId: number }) {
  const { data, isLoading } = useSeoPlanQueries(dossierId, true)
  const [filter, setFilter] = useState('')
  const rows = useMemo(
    () => (data ?? []).filter((q) => !filter || q.query.toLowerCase().includes(filter.toLowerCase())),
    [data, filter],
  )
  if (isLoading)
    return (
      <div className="flex h-24 items-center justify-center">
        <Spinner />
      </div>
    )
  if (!data?.length)
    return <p className="text-sm text-muted-foreground">Geen zoekopdrachten (Search Console niet gekoppeld of geen data).</p>
  return (
    <Card>
      <CardContent className="space-y-3 py-3">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter op zoekopdracht…"
          className="w-full rounded-md border bg-background px-3 py-1.5 text-sm sm:w-72"
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 font-medium">Zoekopdracht</th>
                <th className="py-2 pr-3 font-medium">Pagina</th>
                <th className="py-2 pr-3 text-right font-medium">Klikken</th>
                <th className="py-2 pr-3 text-right font-medium">Vertoningen</th>
                <th className="py-2 pr-3 text-right font-medium">CTR</th>
                <th className="py-2 text-right font-medium">Positie</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={`${q.query}|${q.page}`} className="border-t">
                  <td className="py-1.5 pr-3">{q.query}</td>
                  <td className="max-w-[16rem] truncate py-1.5 pr-3 font-mono text-xs text-muted-foreground">{path(q.page)}</td>
                  <td className="py-1.5 pr-3 text-right">{fmt(q.clicks)}</td>
                  <td className="py-1.5 pr-3 text-right">{fmt(q.impressions)}</td>
                  <td className="py-1.5 pr-3 text-right">{(q.ctr * 100).toFixed(1)}%</td>
                  <td
                    className={cn(
                      'py-1.5 text-right',
                      q.position >= 8 && q.position <= 20 && 'font-medium text-amber-600 dark:text-amber-400',
                    )}
                  >
                    {fmt(q.position, 1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Search Console, laatste 28 dagen, gefilterd op deze site. Oranje: positie 8–20, net rond pagina 1 — daar
          levert verbeteren het snelst iets op.
        </p>
      </CardContent>
    </Card>
  )
}

function SiteView({ dossierId, onBack }: { dossierId: number; onBack: () => void }) {
  const { data, isLoading } = useSeoPlanSite(dossierId)
  const runner = useRun()
  const [statusFilter, setStatusFilter] = useState<'active' | ActionStatus>('active')
  const [phaseFilter, setPhaseFilter] = useState<string>('')
  const [view, setView] = useState<'plan' | 'pages' | 'queries'>('plan')
  const [showInstruction, setShowInstruction] = useState(false)

  const actions = useMemo(() => {
    const all = data?.actions ?? []
    return all.filter(
      (a) =>
        (statusFilter === 'active' ? a.status === 'open' || a.status === 'in_progress' : a.status === statusFilter) &&
        (!phaseFilter || a.phase === phaseFilter),
    )
  }, [data, statusFilter, phaseFilter])

  const phaseCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const a of data?.actions ?? []) {
      if (a.status === 'open' || a.status === 'in_progress') counts[a.phase] = (counts[a.phase] ?? 0) + 1
    }
    return counts
  }, [data])

  if (isLoading || !data)
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Alle sites
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">{data.name}</h1>
            <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:underline">
              {data.url}
            </a>
            <p className="text-xs text-muted-foreground">
              <RunLine run={data.last_run} />
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.last_done_run && (
              <Button
                size="sm"
                variant={showInstruction ? 'default' : 'outline'}
                onClick={() => setShowInstruction(!showInstruction)}
              >
                <Bot className="mr-1 h-4 w-4" />
                Plan voor Claude
              </Button>
            )}
            <RunButton dossierId={data.dossier_id} name={data.name} lastRun={data.last_run} runner={runner} />
          </div>
        </div>
      </div>

      {showInstruction && <InstructionPanel dossierId={data.dossier_id} onClose={() => setShowInstruction(false)} />}

      <SearchCard search={data.search} />
      <CrawlerCard access={data.crawlers} />

      <div className="flex flex-wrap gap-1 border-b">
        {([
          ['plan', 'Plan'],
          ['pages', "Pagina's"],
          ['queries', 'Zoekopdrachten'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              view === key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'pages' && <PagesTable dossierId={data.dossier_id} />}
      {view === 'queries' && <QueriesTable dossierId={data.dossier_id} />}

      {view === 'plan' && (
      <>
      {/* The improvement loop, with the open work per phase. */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={phaseFilter === '' ? 'default' : 'outline'} onClick={() => setPhaseFilter('')}>
          Alle fasen
        </Button>
        {PHASE_ORDER.filter((p) => p in data.phases).map((phase) => (
          <Button
            key={phase}
            size="sm"
            variant={phaseFilter === phase ? 'default' : 'outline'}
            onClick={() => setPhaseFilter(phase)}
          >
            {data.phases[phase]}
            <span className="ml-1.5 text-xs opacity-70">{phaseCounts[phase] ?? 0}</span>
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        {(['active', 'ignored', 'resolved'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              statusFilter === s
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {s === 'active' ? 'Te doen' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {!data.last_done_run ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Nog geen afgeronde analyse. Start er een met de knop hierboven.
          </CardContent>
        </Card>
      ) : actions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-500" />
            <p className="text-sm text-muted-foreground">Niets in deze lijst.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => (
            <ActionCard key={action.id} action={action} phaseLabel={data.phases[action.phase] ?? action.phase} />
          ))}
        </div>
      )}
      </>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function RunAllButton() {
  const startAll = useStartAllSeoPlanRuns()
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={startAll.isPending}
      onClick={() =>
        startAll.mutate(undefined, {
          onSuccess: (r) =>
            toast.success(
              `${r.started} analyse(s) gestart` + (r.already_running ? `, ${r.already_running} liep al` : '') +
                ' — de kaarten werken zichzelf bij.',
            ),
          onError: (e) => toast.error(`Starten mislukt: ${e.message}`),
        })
      }
    >
      <Play className="mr-1 h-4 w-4" />
      Analyseer alle sites
    </Button>
  )
}

export default function SeoPlanPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const site = Number(searchParams.get('site')) || null

  if (site) return <SiteView dossierId={site} onBack={() => setSearchParams({})} />

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          <TrendingUp className="h-7 w-7" />
          SEO-verbeterplan
        </h1>
        <p className="text-muted-foreground">
          Per site wat je moet doen om beter gevonden te worden, volgens de verbeterlus uit{' '}
          <a
            href="https://github.com/i365dev/awesome-seo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            awesome-seo
          </a>
          . Een actie is pas opgelost als de volgende analyse het probleem niet meer ziet.
        </p>
      </div>
      <RunAllButton />
      </div>
      <Overview onOpen={(id) => setSearchParams({ site: String(id) })} />
    </div>
  )
}
